import { AxiosResponse } from "axios";
import { apiErrorWithMessages } from "../lib/apiError";
import { Template, TemplateWithStatistics } from "../models/Template";
import { Paging } from "../models/Paging";
import useApi from "../services/ApiService";
import { TemplateFile } from "../models/TemplateFile";
import { ConflictGroupDto, ConflictResolutionItemDto } from "../models/ConflictGroup";
import { ConflictReasonCount } from "../models/ConflictReasonCount";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import {EmptyFolder} from "../models/EmptyFolder";
import { MigrationFlagStatus } from "../models/Flags/MigrationFlagStatus";

export interface GetTemplatesQueryOptions {
  organizationId?: string;
  status?: string[] | null;
  isLocked?: boolean | null;
  createdBy?: string[];
  templateType?: string[] | null;
  ids?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  hasFlaggedPaths?: boolean | null;
  hasConflicts?: boolean | null;
  /** Only entities whose flag is in one of these states. */
  flagStatus?: MigrationFlagStatus[] | null;
}
export class GetTemplateFilesForTemplateQueryOptions {
  status?: string[] | null;
  extension?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  filePath?: string | null;
  destinationPath?: string | null;
  minFileSize?: number | null;
  maxFileSize?: number | null;
  type?: number | null;
  failureType?: string | null;
  conflictReason?: string[] | null;
  conflictStatus?: string[] | null;
}

export class GetTemplatesLibraryEmptyFoldersForTemplatesLibraryQueryOptions {
  status?: string[] | null;
  uploadPathFromRoot?: string | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class TemplatesRepository {
  private api = useApi();

  async getTemplates(
    options: GetTemplatesQueryOptions = {}
  ): Promise<{ data: Template[]; paging: Paging | null }> {
    const {
      status = null,
      isLocked = null,
      createdBy = null,
      templateType = null,
      ids = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      hasFlaggedPaths = null,
      hasConflicts = null,
      flagStatus = null,
    } = options;

    try {
      const response = await this.api.get("/api/Templates", {
        params: {
          Status: status,
          IsLocked: isLocked,
          CreatedBy: createdBy,
          TemplateType: templateType,
          Id: ids,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          HasFlaggedPaths: hasFlaggedPaths,
          HasConflicts: hasConflicts,
          FlagStatus: flagStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader) {
        paging = JSON.parse(paginationHeader || "");
      }

      return { data, paging };
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch templates");
    }
  }

  async getTemplate(id: string): Promise<TemplateWithStatistics> {
    try {
      const response = await this.api.get(`/api/Templates/${id}`);
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch template with id: ${id}`);
    }
  }

  async getTemplateFiles(
    templateId?: string,
    options: GetTemplateFilesForTemplateQueryOptions = {}
  ): Promise<{ data: TemplateFile[]; paging: Paging | null }> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null
    } = options;
    try {
      const apiUrl = templateId
        ? `/api/Templates/${templateId}/files`
        : "/api/TemplateFiles";
      const response = await this.api.get(apiUrl, {
        params: {
          Status: status,
          Extension: extension,
          FilePath: filePath,
          DestinationPath: destinationPath,
          MinFileSize: minFileSize,
          MaxFileSize: maxFileSize,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          FailureType: failureType,
          ConflictReason: conflictReason,
          ConflictStatus: conflictStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");
      
      return { data, paging };
    } catch (error) {
      const message = templateId
        ? `Failed to fetch files for template with id: ${templateId}`
        : `Failed to fetch template files`;
      throw new Error(message);
    }
  }

  async getTemplatesLibraryEmptyFoldersForTemplatesLibrary(
    id: string,
    options: GetTemplatesLibraryEmptyFoldersForTemplatesLibraryQueryOptions = {}
  ): Promise<{ data: EmptyFolder[]; paging: Paging | null }> {
    const {
      status = null,
      uploadPathFromRoot = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    try {
      const response = await this.api.get(`/api/Templates/${id}/emptyFolders`, {
        params: {
          Status: status,
          UploadPathFromRoot: uploadPathFromRoot,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: 100 * 1000,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      throw new Error(`Failed to fetch empty folder for templates library with id: ${id}`);
    }
  }
  
  async updateTemplateBlock(templateId: string, block: boolean): Promise<void> {
    try {
      const data: Record<string, any> = {
        entityId: templateId,
        block: block,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/Templates/updateBlock`,
        data
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to update block info of template with id: ${templateId}`
      );
    }
  }

  async markNotFoundFileForRetry(
    templateId: string,
    templateFileId: string,
    reason: string
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        migrationEntityId: templateId,
        fileId: templateFileId,
        reason: reason
      };
      await this.api.post(
        `/api/templateFiles/markNotFoundForRetry`,
        data
      );
    } catch (error) {
      throw new Error(`Failed to mark not found file for retry for templateId: ${templateId} and templateFileId: ${templateFileId}`);
    }
  }

  async getConflictGroups(templatesLibraryId: string, includeResolved = false): Promise<ConflictGroupDto[]> {
    try {
      const response = await this.api.get(`/api/TemplateFiles/GetConflictGroups`, {
        params: { templatesLibraryId: templatesLibraryId, includeResolved: includeResolved },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict groups for templates library with id: ${templatesLibraryId}`);
    }
  }

  async getConflictReasonCounts(): Promise<ConflictReasonCount[]> {
    try {
      const response = await this.api.get(`/api/TemplateFiles/GetConflictReasonCounts`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict reason counts for templates libraries`);
    }
  }

  async resolveConflictGroup(resolutions: ConflictResolutionItemDto[]): Promise<void> {
    try {
      await this.api.post(`/api/TemplateFiles/ResolveConflictGroup`, {
        resolutions: resolutions,
      });
    } catch (error) {
      throw apiErrorWithMessages(error, "Failed to resolve conflict group");
    }
  }
}

export default TemplatesRepository;
